import styles from './AdvancedControlCenter.module.scss';

import {
  Checkbox,
  FormControlLabel,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import clsx from 'clsx';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface AdvancedControlCenterSettings {
  autoNumberSuppliers: boolean;
  autoNumberSuppliersERP: boolean;
  autoNumberCustomers: boolean;
  autoNumberCustomersERP: boolean;
  usersEmployees: boolean;
  companyDataGeneral: boolean;
  fillBusinessPartnerNumbers: boolean;
  importMasterData: boolean;
  costCenters: boolean;
  emptyPartsAddresses: boolean;
  clients: boolean;
  branches: boolean;
  numberRanges: boolean;
  planningCalendarSignalText: string;
  soapApiUrl: string;
  locations: boolean;
  updateRules: boolean;
  directories: boolean;
  twoFactorAuthentication: boolean;
  // CRM settings
  addressLinks: boolean;
  salutations: boolean;
  relationships: boolean;
  holidays: boolean;
  fillBusinessPartnerNumbersCRM: boolean;
  setMainContactAutomatically: boolean;
  categoriesCRMObjects: boolean;
  categoriesDocuments: boolean;
  countries: boolean;
  projectRequiredFieldEmails: boolean;
  standardSupplierGroup: string;
  standardCustomerGroup: string;
}

export const AdvancedControlCenter: React.FC = () => {
  const { t } = useTranslation();

  const [settings, setSettings] = useState<AdvancedControlCenterSettings>({
    autoNumberSuppliers: false,
    autoNumberSuppliersERP: false,
    autoNumberCustomers: false,
    autoNumberCustomersERP: false,
    usersEmployees: false,
    companyDataGeneral: false,
    fillBusinessPartnerNumbers: false,
    importMasterData: false,
    costCenters: false,
    emptyPartsAddresses: false,
    clients: false,
    branches: false,
    numberRanges: false,
    planningCalendarSignalText: '',
    soapApiUrl: 'https://api.igento.de/api',
    locations: false,
    updateRules: false,
    directories: false,
    twoFactorAuthentication: false,
    // CRM settings
    addressLinks: false,
    salutations: false,
    relationships: false,
    holidays: false,
    fillBusinessPartnerNumbersCRM: false,
    setMainContactAutomatically: false,
    categoriesCRMObjects: false,
    categoriesDocuments: false,
    countries: false,
    projectRequiredFieldEmails: false,
    standardSupplierGroup: 'A-Lieferanten',
    standardCustomerGroup: 'A-Kunden',
  });

  const handleCheckboxChange =
    (key: keyof AdvancedControlCenterSettings) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSettings((prev) => ({
        ...prev,
        [key]: event.target.checked,
      }));
    };

  const handleTextChange =
    (key: keyof AdvancedControlCenterSettings) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSettings((prev) => ({
        ...prev,
        [key]: event.target.value,
      }));
    };

  const renderCheckboxItem = (
    key: keyof AdvancedControlCenterSettings,
    translationKey: string,
    description?: string
  ) => {
    const hasDescription = Boolean(description);
    const isDisabled = !hasDescription;

    return (
      <div>
        <FormControlLabel
          control={
            isDisabled ? (
              <></>
            ) : (
              <Checkbox
                checked={hasDescription ? (settings[key] as boolean) : false}
                onChange={
                  hasDescription ? handleCheckboxChange(key) : undefined
                }
                color="primary"
                disabled={true}
                sx={{ ml: 1 }}
              />
            )
          }
          label={
            <div>
              <Typography
                variant="body1"
                component="span"
                sx={{
                  color: isDisabled ? 'text.disabled' : 'text.primary',
                }}
              >
                {t(translationKey)}
              </Typography>
              {description && (
                <Typography variant="caption" className={styles.description}>
                  {t(description)}
                </Typography>
              )}
            </div>
          }
          labelPlacement="start"
          sx={{
            margin: 0,
            width: '100%',
            alignItems: hasDescription ? 'flex-start' : 'center',
            justifyContent: 'space-between',
            '& .MuiFormControlLabel-label': {
              flex: 1,
              mr: 2,
            },
          }}
        />
      </div>
    );
  };

  const renderTextFieldItem = (
    key: keyof AdvancedControlCenterSettings,
    translationKey: string,
    description?: string,
    placeholder?: string
  ) => {
    return (
      <div>
        <Typography variant="body1">{t(translationKey)}</Typography>
        {description && (
          <Typography variant="caption">{t(description)}</Typography>
        )}
        <TextField
          fullWidth
          value={settings[key] as string}
          onChange={handleTextChange(key)}
          placeholder={placeholder}
          variant="outlined"
          size="small"
          disabled={true}
        />
      </div>
    );
  };

  return (
    <div>
      <Paper className={styles.paper}>
        <Typography gutterBottom className={styles.header}>
          {t('ADVANCED_CONTROL_CENTER.GENERAL')}
        </Typography>

        {renderCheckboxItem(
          'autoNumberSuppliers',
          'ADVANCED_CONTROL_CENTER.AUTO_NUMBER_SUPPLIERS',
          'ADVANCED_CONTROL_CENTER.AUTO_NUMBER_SUPPLIERS_DESC'
        )}

        {renderCheckboxItem(
          'autoNumberSuppliersERP',
          'ADVANCED_CONTROL_CENTER.AUTO_NUMBER_SUPPLIERS_ERP',
          'ADVANCED_CONTROL_CENTER.AUTO_NUMBER_SUPPLIERS_ERP_DESC'
        )}

        {renderCheckboxItem(
          'autoNumberCustomers',
          'ADVANCED_CONTROL_CENTER.AUTO_NUMBER_CUSTOMERS',
          'ADVANCED_CONTROL_CENTER.AUTO_NUMBER_CUSTOMERS_DESC'
        )}

        {renderCheckboxItem(
          'autoNumberCustomersERP',
          'ADVANCED_CONTROL_CENTER.AUTO_NUMBER_CUSTOMERS_ERP',
          'ADVANCED_CONTROL_CENTER.AUTO_NUMBER_CUSTOMERS_ERP_DESC'
        )}

        {renderCheckboxItem(
          'usersEmployees',
          'ADVANCED_CONTROL_CENTER.USERS_EMPLOYEES'
        )}

        {renderCheckboxItem(
          'companyDataGeneral',
          'ADVANCED_CONTROL_CENTER.COMPANY_DATA_GENERAL'
        )}

        {renderCheckboxItem(
          'fillBusinessPartnerNumbers',
          'ADVANCED_CONTROL_CENTER.FILL_BUSINESS_PARTNER_NUMBERS',
          'ADVANCED_CONTROL_CENTER.FILL_BUSINESS_PARTNER_NUMBERS_DESC'
        )}

        {renderCheckboxItem(
          'importMasterData',
          'ADVANCED_CONTROL_CENTER.IMPORT_MASTER_DATA'
        )}

        {renderCheckboxItem(
          'costCenters',
          'ADVANCED_CONTROL_CENTER.COST_CENTERS'
        )}

        {renderCheckboxItem(
          'emptyPartsAddresses',
          'ADVANCED_CONTROL_CENTER.EMPTY_PARTS_ADDRESSES',
          'ADVANCED_CONTROL_CENTER.EMPTY_PARTS_ADDRESSES_DESC'
        )}

        {renderCheckboxItem('clients', 'ADVANCED_CONTROL_CENTER.CLIENTS')}

        {renderCheckboxItem('branches', 'ADVANCED_CONTROL_CENTER.BRANCHES')}

        {renderCheckboxItem(
          'numberRanges',
          'ADVANCED_CONTROL_CENTER.NUMBER_RANGES'
        )}

        {renderTextFieldItem(
          'planningCalendarSignalText',
          'ADVANCED_CONTROL_CENTER.PLANNING_CALENDAR_SIGNAL_TEXT',
          'ADVANCED_CONTROL_CENTER.PLANNING_CALENDAR_SIGNAL_TEXT_DESC'
        )}

        {renderTextFieldItem(
          'soapApiUrl',
          'ADVANCED_CONTROL_CENTER.SOAP_API_URL',
          'ADVANCED_CONTROL_CENTER.SOAP_API_URL_DESC',
          'https://api.igento.de/api'
        )}

        {renderCheckboxItem('locations', 'ADVANCED_CONTROL_CENTER.LOCATIONS')}

        {renderCheckboxItem(
          'updateRules',
          'ADVANCED_CONTROL_CENTER.UPDATE_RULES'
        )}

        {renderCheckboxItem(
          'directories',
          'ADVANCED_CONTROL_CENTER.DIRECTORIES'
        )}

        {renderCheckboxItem(
          'twoFactorAuthentication',
          'ADVANCED_CONTROL_CENTER.TWO_FACTOR_AUTHENTICATION',
          'ADVANCED_CONTROL_CENTER.TWO_FACTOR_AUTHENTICATION_DESC'
        )}
      </Paper>

      <Paper className={clsx(styles.paper, styles.crm)}>
        <Typography gutterBottom className={styles.header}>
          {t('ADVANCED_CONTROL_CENTER.CRM')}
        </Typography>

        {renderCheckboxItem(
          'addressLinks',
          'ADVANCED_CONTROL_CENTER.ADDRESS_LINKS'
        )}

        {renderCheckboxItem(
          'salutations',
          'ADVANCED_CONTROL_CENTER.SALUTATIONS'
        )}

        {renderCheckboxItem(
          'relationships',
          'ADVANCED_CONTROL_CENTER.RELATIONSHIPS'
        )}

        {renderCheckboxItem('holidays', 'ADVANCED_CONTROL_CENTER.HOLIDAYS')}

        {renderCheckboxItem(
          'fillBusinessPartnerNumbersCRM',
          'ADVANCED_CONTROL_CENTER.FILL_BUSINESS_PARTNER_NUMBERS_CRM',
          'ADVANCED_CONTROL_CENTER.FILL_BUSINESS_PARTNER_NUMBERS_CRM_DESC'
        )}

        {renderCheckboxItem(
          'setMainContactAutomatically',
          'ADVANCED_CONTROL_CENTER.SET_MAIN_CONTACT_AUTOMATICALLY',
          'ADVANCED_CONTROL_CENTER.SET_MAIN_CONTACT_AUTOMATICALLY_DESC'
        )}

        {renderCheckboxItem(
          'categoriesCRMObjects',
          'ADVANCED_CONTROL_CENTER.CATEGORIES_CRM_OBJECTS'
        )}

        {renderCheckboxItem(
          'categoriesDocuments',
          'ADVANCED_CONTROL_CENTER.CATEGORIES_DOCUMENTS'
        )}

        {renderCheckboxItem('countries', 'ADVANCED_CONTROL_CENTER.COUNTRIES')}

        {renderCheckboxItem(
          'projectRequiredFieldEmails',
          'ADVANCED_CONTROL_CENTER.PROJECT_REQUIRED_FIELD_EMAILS'
        )}

        {renderTextFieldItem(
          'standardSupplierGroup',
          'ADVANCED_CONTROL_CENTER.STANDARD_SUPPLIER_GROUP',
          'ADVANCED_CONTROL_CENTER.STANDARD_SUPPLIER_GROUP_DESC',
          'A-Lieferanten'
        )}

        {renderTextFieldItem(
          'standardCustomerGroup',
          'ADVANCED_CONTROL_CENTER.STANDARD_CUSTOMER_GROUP',
          'ADVANCED_CONTROL_CENTER.STANDARD_CUSTOMER_GROUP_DESC',
          'A-Kunden'
        )}
      </Paper>
    </div>
  );
};
